import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Input } from '@/components/ui/input'
import type { CompanySuggestion, CompanyValues } from '../types'
import { searchCompanies } from '../api'

interface Props {
  value?: CompanyValues | Record<string, never>
  onChange: (c: CompanyValues | null) => void
  placeholder?: string
  limit?: number
  disabled?: boolean
}

export function CompanyAutocomplete({ value, onChange, placeholder, limit = 10, disabled }: Props) {
  const [query, setQuery] = useState(() => buildLabel(value))
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<CompanySuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [focused, setFocused] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)

  // Mirror the external value into the input whenever it changes from outside
  // (selection, reset/"Vyčistit formulář", session switch). Skip while the user
  // is typing so their keystrokes aren't clobbered — including clearing to empty.
  useEffect(() => {
    if (!focused) setQuery(buildLabel(value))
  }, [value, focused])

  useEffect(() => {
    function handler(e: MouseEvent) {
      const t = e.target as Node
      if (wrapperRef.current?.contains(t) || dropdownRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // The dropdown is portalled to <body> (so a card's content-visibility paint
  // containment can't clip it). Position it under the input and follow on
  // scroll/resize.
  useEffect(() => {
    if (!open) return
    function place() {
      const el = wrapperRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left, width: r.width })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  function onInput(v: string) {
    setQuery(v)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (v.trim().length < 2) {
      setItems([])
      setOpen(false)
      onChange(null)
      return
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const list = await searchCompanies(v, limit)
        setItems(list)
        setOpen(list.length > 0)
      } catch {
        setItems([])
        setOpen(false)
      } finally {
        setLoading(false)
      }
    }, 250)
  }

  function select(s: CompanySuggestion) {
    const values = s.values ?? {}
    onChange(values)
    setQuery(buildLabel(values))
    setOpen(false)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        type="text"
        value={query}
        onChange={(e) => onInput(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => {
          setFocused(true)
          if (items.length > 0) setOpen(true)
        }}
        onBlur={() => setFocused(false)}
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400">…</div>
      )}
      {open && items.length > 0 && pos &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 1000 }}
            className="max-h-64 overflow-auto rounded-md border border-zinc-200 bg-white shadow-lg"
          >
            {items.map((s, i) => {
              const v = s.values ?? {}
              return (
                <button
                  key={`${v.COMPANY_REGISTRATION_NUMBER ?? ''}-${i}`}
                  type="button"
                  onClick={() => select(s)}
                  className="block w-full px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <span className="block text-xs font-medium text-zinc-900">{v.COMPANY_NAME ?? '—'}</span>
                  {v.COMPANY_REGISTRATION_NUMBER && (
                    <span className="block text-[11px] text-zinc-500">IČO: {v.COMPANY_REGISTRATION_NUMBER}</span>
                  )}
                  {v.ADDRESS_WHOLE_ADDRESS && (
                    <span className="block text-[11px] text-zinc-500">{v.ADDRESS_WHOLE_ADDRESS}</span>
                  )}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </div>
  )
}

function buildLabel(c?: CompanyValues | Record<string, never>): string {
  if (!c || Object.keys(c).length === 0) return ''
  const name = (c as CompanyValues).COMPANY_NAME
  const ico = (c as CompanyValues).COMPANY_REGISTRATION_NUMBER
  if (name && ico) return `${name} (${ico})`
  return name ?? ico ?? ''
}
